import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiLineLiffService } from '../services/api/Line-liff/api-line-liff';
import { ApiUserService } from '../services/api/User/api-user';
import { LiffService } from '../services/liff.service';
import { environment } from '../../../environments/environment';

const LOCAL_STORAGE_KEYS = [
  'token_user',
  'login_status',
  'line_uid',
  'display_name',
  'picture_url',
];

function clearSession(cookieService: CookieService): void {
  cookieService.delete('login_status');
  cookieService.delete('token_user');
  cookieService.delete('line_uid');
  cookieService.delete('display_name');
  cookieService.delete('picture_url');
  LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

function resolveLineUid(cookieService: CookieService): string {
  return (
    cookieService.get('line_uid') || localStorage.getItem('line_uid') || ''
  ).trim();
}

/** Private routes require LINE member cookie + known line_uid (from LIFF entry). */
export const AuthGuard: CanActivateFn = () => {
  const cookieService = inject(CookieService);
  const router = inject(Router);
  const apiLineLiffService = inject(ApiLineLiffService);
  const apiUserService = inject(ApiUserService);
  const liffService = inject(LiffService);

  /*#################################################### Function ################################################*/

  // 1. Mock/Dev Mode Sync: อัปเดตข้อมูลผู้ใช้ตาม environment.ts ทันทีทุกครั้งที่มีการเปิดใช้งาน/เปลี่ยนหน้า (เมื่อ useMockLiff = true)
  if (environment.useMockLiff) {
    liffService.initLiff();
  }

  // 2. Fast Check: ตรวจสอบ line_uid จาก Cookie/LocalStorage หากไม่มีให้ Redirect ทันที (0ms)
  const lineUid = resolveLineUid(cookieService);
  if (!lineUid) {
    clearSession(cookieService);
    return of(router.createUrlTree(['/register']));
  }

  // 3. Light Verification: ตรวจสอบสถานะ Token กับระบบหลังบ้านทุกครั้งที่เข้ามาใช้งาน
  return apiLineLiffService.verifyToken().pipe(
    map((verifyRes) => {
      // หาก Token หมดอายุหรือไม่ถูกต้อง ให้ล้างเซสชันแล้วไปหน้า register
      if (verifyRes.code !== '000') {
        clearSession(cookieService);
        return router.createUrlTree(['/register']);
      }

      // บันทึกสถานะล็อกอิน
      cookieService.set('login_status', 'logged_in');
      localStorage.setItem('login_status', 'logged_in');

      // 4. Background Sync (Non-blocking): อัปเดตข้อมูลโปรไฟล์ในพื้นหลังโดยไม่บล็อกการโหลดเปลี่ยนหน้า
      const name = cookieService.get('display_name') || localStorage.getItem('display_name') || '';
      const pictureUrl = localStorage.getItem('picture_url') || cookieService.get('picture_url') || '';
      if (name) {
        apiUserService.updateNameApi(lineUid, name, pictureUrl).subscribe({
          error: (err) => console.warn('[AuthGuard] Background profile sync error:', err)
        });
      }

      // อนุญาตให้เข้าถึงหน้าได้ทันที (รวดเร็ว)
      return true;
    }),
    catchError(() => {
      clearSession(cookieService);
      return of(router.createUrlTree(['/register']));
    })
  );

  /*############################################# API #################################################*/
};
